import { useRef, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Form,
  FormControl,
  FormGroup,
  ShortTextFormField
} from "@client:common/form";
import { Button, IconButton } from "@client:common/button";
import { Validators } from "@client:common/form/validation";
import { Ripple } from "@client:common/effects/ripple";
import { Markdown } from "@client:common/Markdown";
import { Icon } from "@client:common/icon";

import "./AccountVerification.dark.scss";
import "./AccountVerification.light.scss";

interface Props {
  onSubmit: (username: string, verificationCode: string) => void;
  onResendVerificationCode: (username: string) => void;
}

export function AccountVerification(props: Props) {
  const [searchParams] = useSearchParams();
  const email = new URLSearchParams(searchParams).get("email") ?? "";
  const cognitoUsername = email.split("@")[0];
  const formGroupRef = useRef(
    new FormGroup({
      code: new FormControl("", [Validators.checkNotEmpty()])
    })
  );
  const [isFormValid, setIsFormValid] = useState(
    formGroupRef.current.isValid()
  );
  const onResendVerificationCode = () => {
    props.onResendVerificationCode(cognitoUsername);
  };
  const onSubmit = () => {
    props.onSubmit(
      cognitoUsername,
      formGroupRef.current.findControl("code").getValue()
    );
  };

  const [isMobile, setIsMobile] = useState(false);

  const updateIsMobile = () => {
    const screenWidth = window.innerWidth;
    const updatedIsMobile = screenWidth <= 550;
    setIsMobile(updatedIsMobile);
  };
  useEffect(() => {
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    const subscription = formGroupRef.current.validityChanges().subscribe({
      next: setIsFormValid
    });
    return () => {
      window.removeEventListener("resize", updateIsMobile);
      subscription.unsubscribe();
    };
  }, []);

  if (email.length === 0 || !email.includes("@")) {
    return null;
  }

  const message = [
    `We just sent a code to \n**${email}**.<br />`,
    "This email could take up to 30 minutes to arrive.<br />Feel free to come back later."
  ].join(" ");

  return (
    <div className="account-verification">
      <header>
        <h2>Enter Verification Code</h2>
        <Markdown className="markdown-message">{message}</Markdown>
      </header>
      <Form
        className="account-verification__form"
        formGroup={formGroupRef.current}
        onSubmit={onSubmit}
      >
        <ShortTextFormField
          label=""
          formControl={formGroupRef.current.findControl("code")}
        />
        {!isMobile && (
          <Button
            style="basic"
            color="primary"
            className="new-code-trigger"
            onClick={onResendVerificationCode}
          >
            <Ripple />
            SEND ME A NEW CODE
          </Button>
        )}
        <div className="account-verification__action-container">
          {isMobile && (
            <Button
              style="basic"
              color="primary"
              className="new-code-trigger"
              onClick={onResendVerificationCode}
            >
              <Ripple />
              SEND ME A NEW CODE
            </Button>
          )}
          <IconButton
            style="raised"
            color="primary"
            disabled={!isFormValid}
            onClick={onSubmit}
          >
            <Ripple />
            <Icon icon="login" />
            CREATE ACCOUNT
          </IconButton>
        </div>
      </Form>
    </div>
  );
}
